import { useMemo } from "react";
import type { DemandAnalysis, HeadwayBand } from "../../api/types";
import {
  type PlanParams,
  fleetFor,
  fmtHHMM,
  fmtPct,
  fmtRiders,
  sameBands,
  suggestBands,
} from "./demandMath";

interface Props {
  demand: DemandAnalysis;
  plan: PlanParams;
  onPlan: (p: PlanParams) => void;
  /** The service window to cover, in minutes after midnight. */
  serviceStartMin: number;
  serviceEndMin: number;
  /** The bands the generator currently holds, to show what would change. */
  currentBands: HeadwayBand[] | null;
  /** Scheduled round trip including turnarounds, when the generator knows it. */
  roundTripMin: number | null;
  /** Null when the viewer cannot edit schedules: the plan is read-only. */
  onApply: ((bands: HeadwayBand[]) => void) | null;
}

function NumberField({
  label, value, suffix, min, max, step = 1, onChange,
}: {
  label: string;
  value: number;
  suffix: string;
  min: number;
  max: number;
  step?: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="dm-param">
      <span>{label}</span>
      <div className="sched-suffix-field">
        <input type="number" min={min} max={max} step={step} value={value}
          onChange={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v)) onChange(Math.min(max, Math.max(min, v)));
          }} />
        <span className="sched-suffix">{suffix}</span>
      </div>
    </label>
  );
}

/**
 * The headways this demand calls for, as bands the generator can take.
 *
 * Each hour is sized for its busier direction at its busiest point — the
 * generator runs one headway both ways, so the heavier flow sets it — and
 * neighbouring hours that come out the same are merged.
 */
export default function HeadwayPlan({
  demand, plan, onPlan, serviceStartMin, serviceEndMin, currentBands, roundTripMin, onApply,
}: Props) {
  const bands = useMemo(
    () => suggestBands(demand.buckets, demand.bucket_minutes, serviceStartMin, serviceEndMin, plan),
    [demand, serviceStartMin, serviceEndMin, plan]
  );

  const tightest = bands.reduce((min, b) => Math.min(min, b.headway_min), Infinity);
  const fleet = roundTripMin && Number.isFinite(tightest) ? fleetFor(tightest, roundTripMin) : null;
  const unchanged = currentBands != null && sameBands(currentBands, bands);
  const anyOver = bands.some((b) => b.overMinimum);

  /** The headway the generator currently has at a given minute, if any. */
  const currentAt = (min: number): number | null =>
    currentBands?.find((b) => min >= b.from_min && min < b.to_min)?.headway_min ?? null;

  return (
    <div className="dm-chart dm-plan">
      <div className="dm-chart-head">
        <div>
          <h4>Headways this demand needs</h4>
          <p className="dm-chart-sub">
            Each hour sized for its busier direction, with buses planned to {fmtPct(plan.targetLoad)} of{" "}
            {plan.busCapacity} riders.
          </p>
        </div>
      </div>

      <div className="dm-params">
        <NumberField label="Bus capacity" suffix="riders" min={10} max={300} value={plan.busCapacity}
          onChange={(v) => onPlan({ ...plan, busCapacity: Math.round(v) })} />
        <NumberField label="Plan to" suffix="% full" min={40} max={120} step={5} value={Math.round(plan.targetLoad * 100)}
          onChange={(v) => onPlan({ ...plan, targetLoad: v / 100 })} />
        <NumberField label="Tightest headway" suffix="min" min={1} max={30} value={plan.minHeadway}
          onChange={(v) => onPlan({ ...plan, minHeadway: Math.round(v) })} />
        <NumberField label="Widest headway" suffix="min" min={2} max={60} value={plan.maxHeadway}
          onChange={(v) => onPlan({ ...plan, maxHeadway: Math.round(v) })} />
      </div>

      {bands.length === 0 ? (
        <div className="empty">No service window to plan for.</div>
      ) : (
        <table className="data-table compact dm-table dm-plan-table">
          <thead>
            <tr>
              <th>From – to</th>
              <th className="num">Needs a bus every</th>
              {currentBands && <th className="num">Generator has it every</th>}
              <th className="num">Busiest hour</th>
              <th className="num">Load</th>
            </tr>
          </thead>
          <tbody>
            {bands.map((b) => {
              const now = currentAt(b.from_min);
              return (
                <tr key={b.from_min}>
                  <td>{fmtHHMM(b.from_min)} – {fmtHHMM(b.to_min)}</td>
                  <td className="num"><strong>{b.headway_min} min</strong></td>
                  {currentBands && (
                    <td className={`num${now != null && now !== b.headway_min ? "" : " dm-same"}`}>
                      {now == null ? "–" : `${now} min`}
                    </td>
                  )}
                  <td className="num">{fmtRiders(b.ridersPerHour)}</td>
                  <td className="num">
                    {b.overMinimum ? <span className="dm-flag">▲ {fmtPct(b.loadFactor)}</span> : fmtPct(b.loadFactor)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div className="dm-plan-foot">
        {fleet != null && (
          <p>
            Running every {tightest} min at the peak takes about <strong>{fleet} buses</strong> on the road
            ({roundTripMin}-minute round trip).
          </p>
        )}
        {anyOver && (
          <p className="dm-flag">
            ▲ Even at the tightest headway, some hours stay above the planned load. A larger bus or a tighter
            minimum would be needed.
          </p>
        )}
        {onApply && (
          <div className="dm-plan-actions">
            <button type="button" className="btn primary sched-preview-btn"
              disabled={bands.length === 0 || unchanged}
              onClick={() => onApply(bands.map(({ from_min, to_min, headway_min }) => ({ from_min, to_min, headway_min })))}>
              {unchanged ? "Generator already uses these bands" : "Use these bands in the generator"}
            </button>
            <span className="dm-legend-note">
              Sets the bands and dispatches both directions on every headway.
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
